import classNames from 'classnames';
import React, { useState } from 'react';
import './index.scss';
import EcoImg from 'eco-design/base/eco-img';
import EcoImgVideo from 'eco-design/base/eco-img-video';
import gtm from 'eco-design-reliance/gtm';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper';
import 'swiper/scss';
import 'swiper/scss/pagination';

export interface EcoTabsImgSwiperItem {
    tabLabel: string;
    tabValue: string;
    description?: string;
    tabTip?: string;
    isVideo?: boolean;
    tabImg: {
        pc?: string;
        pad?: string;
        h5?: string;
    };
    mainImg: {
        pc?: string;
        pad?: string;
        h5?: string;
        poster?: string;
        posterH5?: string;
        isOpenLazy?: boolean;
    };
    tipLeftTitle?: string;
    tipRightTitle?: string;
    tipTheme?: 'white' | 'black';
}

export interface EcoTabsImgSwiperProps {
    className?: string;
    theme?: 'white' | 'black';
    title?: React.ReactNode;
    subtitle?: React.ReactNode;
    items?: EcoTabsImgSwiperItem[];
}

/**
 * 维护: 杨柏迪
 * 描述: tab-swiper 切换
 */
const DEFAULT_ITEMS: EcoTabsImgSwiperProps['items'] = [];
const EcoTabsImgSwiper: React.FC<EcoTabsImgSwiperProps> = ({ className, title, subtitle, items = DEFAULT_ITEMS }) => {
    const prefixCls = `eco-tabs-img-swiper`;
    const classes = classNames(prefixCls, className);
    const [swiper, setSwiper] = useState<any>(null);
    const [currentIndex, setCurrentIndex] = useState(0);

    // 初始化 Swiper
    const handleSwiper = (swiperInstance: any) => {
        setSwiper(swiperInstance);
        setCurrentIndex(swiperInstance.realIndex);
    };

    // Swiper 切换时更新索引
    const handleSlideChange = (swiper: any) => {
        setCurrentIndex(swiper.realIndex);
    };

    // 点击图片 tab 切换
    const handleTabClick = (index: number) => {
        if (swiper) {
            if (swiper.params.loop) {
                swiper.slideToLoop(index);
            } else {
                swiper.slideTo(index);
            }
        }
        gtm.push({
            event: 'uaEvent',
            event_name: 'product_detail_page',
            list_name: title || 'none',
            button_name: `Tab ${index + 1}`,
        });
    };

    return (
        <div className={classes}>
            <div className={`${prefixCls}-header`}>
                {title && <div className={`${prefixCls}-title`}>{title}</div>}
                {subtitle && <div className={`${prefixCls}-subtitle`}>{subtitle}</div>}
            </div>
            {items.length > 0 && (
                <>
                    <div className={`${prefixCls}-swiper-container`}>
                        <Swiper
                            className={`${prefixCls}-swiper`}
                            loop={false}
                            pagination={{
                                el: `.${prefixCls}-swiper-pagination`,
                                clickable: true,
                            }}
                            modules={[Pagination]}
                            onSwiper={handleSwiper}
                            onSlideChange={handleSlideChange}
                            initialSlide={0}
                        >
                            {items.map((item, idx) => (
                                <SwiperSlide key={idx}>
                                    <div className={`${prefixCls}-main-img`}>
                                        <EcoImgVideo {...item.mainImg} isVideo={item?.isVideo || false} />
                                    </div>
                                    <div className={`${prefixCls}-main-tips`}>
                                        <div className={`${prefixCls}-main-tips-box ${prefixCls}-main-tips-${item.tipTheme}`}>
                                            <div className={`${prefixCls}-main-tips-left-title`}>{item.tipLeftTitle}</div>
                                            <div className={`${prefixCls}-main-tips-right-title`}>{item.tipRightTitle}</div>
                                        </div>
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                        <div className={`${prefixCls}-img-tabs-container`}>
                            <div className={`${prefixCls}-img-tabs`}>
                                {items.map((item, idx) => (
                                    <div
                                        key={idx}
                                        className={classNames(`${prefixCls}-img-tab`, {
                                            active: idx === currentIndex,
                                        })}
                                        onClick={() => handleTabClick(idx)}
                                    >
                                        <EcoImg {...item.tabImg} isOpenLazy />
                                        {idx !== currentIndex && <div className={`${prefixCls}-img-tab-title`}>{item.tabLabel}</div>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className={`${prefixCls}-swiper-pagination`}></div>

                    {items[currentIndex]?.tabLabel && (
                        <div className={`${prefixCls}-content`}>
                            {items[currentIndex].tabLabel && <div className={`${prefixCls}-tab-title`}>{items[currentIndex].tabLabel}</div>}
                            {items[currentIndex].description && <div className={`${prefixCls}-tab-description`}>{items[currentIndex].description}</div>}
                            {items[currentIndex].tabTip && <div className={`${prefixCls}-tab-tip`}>{items[currentIndex].tabTip}</div>}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default EcoTabsImgSwiper;
